import React from 'react'

interface InsightDotsProps {
  total: number
  currentIndex: number
  className?: string
}

/**
 * Pinterest/Instagram-style progress dots indicator
 * Shows position in slideshow with smooth animations
 */
const InsightDots: React.FC<InsightDotsProps> = ({
  total,
  currentIndex,
  className = ''
}) => {
  // Limit dots display for very long slideshows (show max 8 dots with ellipsis)
  const maxDots = 8
  const shouldTruncate = total > maxDots

  const getVisibleDots = () => {
    if (!shouldTruncate) {
      return Array.from({ length: total }, (_, i) => i)
    }

    // Smart truncation: always show first, last, and current ± 2
    const dots: (number | 'ellipsis')[] = []

    if (currentIndex < 4) {
      // Near start: show first 6 + ellipsis + last
      dots.push(0, 1, 2, 3, 4, 5, 'ellipsis', total - 1)
    } else if (currentIndex > total - 5) {
      // Near end: show first + ellipsis + last 6
      dots.push(0, 'ellipsis', total - 6, total - 5, total - 4, total - 3, total - 2, total - 1)
    } else {
      // Middle: show first + ellipsis + current-1, current, current+1 + ellipsis + last
      dots.push(0, 'ellipsis', currentIndex - 1, currentIndex, currentIndex + 1, 'ellipsis', total - 1)
    }

    return dots
  }

  const visibleDots = getVisibleDots()

  return (
    <div
      className={`flex items-center justify-center gap-1.5 ${className}`}
      aria-label={`Slide ${currentIndex + 1} of ${total}`}
      role="navigation"
    >
      {visibleDots.map((dotIndex, idx) => {
        if (dotIndex === 'ellipsis') {
          return (
            <div
              key={`ellipsis-${idx}`}
              className="flex items-center gap-0.5 px-1"
            >
              <div className="w-1 h-1 rounded-full bg-white/30" />
              <div className="w-1 h-1 rounded-full bg-white/30" />
              <div className="w-1 h-1 rounded-full bg-white/30" />
            </div>
          )
        }

        const isActive = dotIndex === currentIndex

        return (
          <div
            key={dotIndex}
            className={`rounded-full transition-all duration-300 ease-out ${
              isActive
                ? 'w-2 h-2 bg-white scale-150 shadow-lg'
                : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/60'
            }`}
            style={{
              transform: isActive ? 'scale(1.5)' : 'scale(1)',
            }}
          />
        )
      })}
    </div>
  )
}

export default InsightDots
